import { useMemo, useState } from 'react';
import {
  VegaButton,
  VegaCard,
  VegaFlex,
  VegaFont,
  VegaGrid,
  VegaPageNotification,
  VegaSegmentControl,
  VegaTable,
} from '@globalpayments/vega-react';
import type {
  VegaTableColumnConfig,
  VegaTableDataSourceItem,
} from '@globalpayments/vega/dist/types/components/vega-table/interface';

import DetailsModal from '../../../components/detailsModal';
import type {
  HorizonOptOutDateRange,
  HorizonOptOutDisplayRow,
  HorizonOptOutType,
  HorizonRegion,
  HorizonTimeZone,
} from '../../../types/horizonOptOutTracker';
import {
  createHorizonOptOutCsv,
  formatHorizonOptOutTime,
  getHorizonRecordFlag,
  maskEmail,
} from '../../../utility/horizonOptOutTracker';
import { chipColors, singlePageExtensions, tablePagination } from '../constants';

interface HorizonOptOutTableProps {
  dateRange: HorizonOptOutDateRange;
  hasSearchFilter: boolean;
  isEnriching: boolean;
  rows: HorizonOptOutDisplayRow[];
}

interface HorizonOptOutTableRecord extends VegaTableDataSourceItem {
  appUid: string;
  companyName: string;
  email: string;
  optOutReason: string;
  optOutTime: string;
  recordFlag: string;
  region: string;
  type: string;
}

const isRegion = (value: unknown): value is HorizonRegion => value === 'US' || value === 'UK';
const isOptOutType = (value: unknown): value is HorizonOptOutType =>
  value === 'Worth' || value === 'Worth unavailable' || value === 'Global Payments';

const optOutTypeDescriptions: Record<HorizonOptOutType, string> = {
  'Global Payments': 'A Global Payments rule opted the application out. Worth did not make this decision.',
  Worth: 'Worth evaluated the application and returned a decision that opted it out.',
  'Worth unavailable': 'The Worth integration failed, so Worth never reached a decision. This is not a Worth opt-out.',
};

const createChip = (
  createElement: Parameters<NonNullable<VegaTableColumnConfig['render']>>[0],
  value: HorizonRegion | HorizonOptOutType,
  ariaLabel: string = value,
) =>
  createElement('vega-chip', {
    'aria-label': ariaLabel,
    bgColor: chipColors[value].bgColor,
    chipType: 'label',
    size: 'extra-small',
    text: value,
    textColor: chipColors[value].textColor,
    variant: 'filled-background',
  });

const createBaseColumns = (timeZone: HorizonTimeZone): VegaTableColumnConfig[] => [
  {
    key: 'appUid',
    label: 'App ID',
    prop: 'appUid',
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    breakpoint: 'L',
    width: '260px',
  },
  {
    key: 'email',
    label: 'Email',
    prop: 'email',
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    breakpoint: 'M',
    width: '240px',
  },
  {
    key: 'companyName',
    label: 'DBA Name',
    prop: 'companyName',
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    width: '220px',
  },
  {
    key: 'region',
    label: 'Region',
    prop: 'region',
    breakpoint: 'S',
    width: '100px',
    render: (createElement, value) =>
      isRegion(value) ? createChip(createElement, value, `Horizon ${value}`) : String(value ?? 'N/A'),
  },
  {
    key: 'recordFlag',
    label: 'Record flag',
    prop: 'recordFlag',
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    breakpoint: 'L',
    width: '200px',
  },
  {
    key: 'optOutReason',
    label: 'Opt out reason',
    prop: 'optOutReason',
    overflow: 'ellipsis',
    breakpoint: 'M',
    width: '360px',
    render: (createElement, value) =>
      createElement(
        'vega-tooltip',
        {
          maxWidth: 420,
          placement: 'top',
          style: {
            display: 'block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          text: String(value ?? 'N/A'),
          trigger: 'hover',
        },
        String(value ?? 'N/A'),
      ),
  },
  {
    key: 'type',
    label: 'Triggered By',
    prop: 'type',
    width: '160px',
    render: (createElement, value) =>
      isOptOutType(value)
        ? createElement(
            'vega-tooltip',
            { maxWidth: 360, placement: 'top', text: optOutTypeDescriptions[value], trigger: 'hover' },
            createChip(createElement, value),
          )
        : String(value ?? 'N/A'),
  },
  {
    key: 'optOutTime',
    label: `Triggered At (${timeZone})`,
    prop: 'optOutTime',
    breakpoint: 'M',
    width: '200px',
  },
];

const createTableData = (rows: HorizonOptOutDisplayRow[], timeZone: HorizonTimeZone): HorizonOptOutTableRecord[] =>
  rows.map((row) => ({
    appUid: row.appUid,
    companyName: row.companyName,
    email: maskEmail(row.email),
    optOutReason:
      row.detailStatus === 'loading'
        ? 'Loading...'
        : row.detailStatus === 'error'
          ? 'Unable to load'
          : row.detailStatus === 'unresolved'
            ? 'Unresolved'
            : (row.optOutReason ?? 'N/A'),
    optOutTime:
      row.detailStatus === 'loaded' && row.optOutTimeUTC ? formatHorizonOptOutTime(row.optOutTimeUTC, timeZone) : 'N/A',
    recordFlag: getHorizonRecordFlag(row),
    region: row.region,
    type: row.detailStatus === 'loaded' ? (row.type ?? 'N/A') : row.detailStatus === 'unresolved' ? 'Unresolved' : 'N/A',
  }));

const downloadCsv = (
  rows: HorizonOptOutTableRecord[],
  dateRange: HorizonOptOutDateRange,
  timeZone: HorizonTimeZone,
) => {
  const csvUrl = URL.createObjectURL(
    new Blob([`\uFEFF${createHorizonOptOutCsv(rows, timeZone)}`], { type: 'text/csv;charset=utf-8' }),
  );
  const downloadLink = document.createElement('a');
  downloadLink.download = `horizon-opt-outs-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
  downloadLink.href = csvUrl;
  document.body.append(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  window.setTimeout(() => URL.revokeObjectURL(csvUrl), 0);
};

/** Displays paginated tracker results with CSV export and application details. */
export const HorizonOptOutTable = ({ dateRange, hasSearchFilter, isEnriching, rows }: HorizonOptOutTableProps) => {
  const [detailsAppUid, setDetailsAppUid] = useState<string>();
  const [pageSize, setPageSize] = useState<number>(tablePagination.pageSize);
  const [timeZone, setTimeZone] = useState<HorizonTimeZone>('UTC');
  const tableData = useMemo(() => createTableData(rows, timeZone), [rows, timeZone]);
  const hasMultiplePages = tableData.length > pageSize;
  const pagination = useMemo(
    () => ({
      ...tablePagination,
      extensions: hasMultiplePages ? tablePagination.extensions : singlePageExtensions,
      pageSize,
      vegaPageSizeChange: (event: CustomEvent<number>) => setPageSize(event.detail),
    }),
    [hasMultiplePages, pageSize],
  );
  const detailsRow = rows.find((row) => row.appUid === detailsAppUid);
  const columns: VegaTableColumnConfig[] = useMemo(
    () => [
      ...createBaseColumns(timeZone),
      {
        key: 'details',
        label: 'Details',
        width: '140px',
        render: (createElement, _value, record) =>
          createElement('vega-button-link', {
            'aria-label': `View technical details for ${String(record.companyName)}`,
            label: 'View details',
            onVegaClick: () => setDetailsAppUid(String(record.appUid)),
            size: 'small',
          }),
      },
    ],
    [timeZone],
  );

  return (
    <VegaCard padding={{ default: 'size-16', M: 'size-24' }} variant="border">
      <VegaFlex direction="col" gap="size-16">
        {isEnriching && (
          <VegaFont as="p" color="text-secondary" variant="font-p2-short">
            Loading opt-out details for the selected date range...
          </VegaFont>
        )}
        <VegaFont as="p" color="text-secondary" data-testid="horizon-record-count" variant="font-p2-short">
          {rows.length} record{rows.length === 1 ? '' : 's'} found
        </VegaFont>
        {rows.length === 0 ? (
          !isEnriching && (
            <VegaPageNotification
              duration={0}
              message={
                hasSearchFilter
                  ? 'No opt-outs match the applied search filters.'
                  : 'No disqualified Horizon applications were found.'
              }
              notificationTitle={hasSearchFilter ? 'No matching opt-outs' : 'No applications found'}
              type="info"
            />
          )
        ) : (
          <>
            <VegaFlex
              alignItems={{ default: 'stretch', M: 'center' }}
              direction={{ default: 'column', M: 'row' }}
              gap="size-12"
              justifyContent={{ default: 'start', M: 'end' }}
            >
              <VegaSegmentControl
                aria-label="Timestamp time zone"
                data-testid="horizon-time-zone"
                onVegaChange={(event) => {
                  if (event.detail === 'UTC' || event.detail === 'Local') setTimeZone(event.detail);
                }}
                segments={[
                  { key: 'UTC', text: 'UTC' },
                  { key: 'Local', text: 'Local time' },
                ]}
                selected={timeZone}
                size="small"
                variant="text-only"
              />
              <VegaButton
                disabled={isEnriching}
                icon="fa-solid fa-download"
                label="Export CSV"
                onVegaClick={() => downloadCsv(tableData, dateRange, timeZone)}
                size="small"
                variant="secondary"
              />
            </VegaFlex>
            <VegaTable
              aria-label="Horizon opt-outs"
              columns={columns}
              dataSource={tableData}
              density="compact"
              pagination={pagination}
              rowClickable={false}
              rowDivider
              rowKey="appUid"
              striped
            />
          </>
        )}
        <DetailsModal
          modalTitle={detailsRow ? `${detailsRow.companyName} details` : 'Application details'}
          onClose={() => setDetailsAppUid(undefined)}
          open={detailsRow !== undefined}
          width={{ default: '90%', M: '50%' }}>
          {detailsRow && (
            <VegaGrid column={{ default: 1, M: 2 }} gap="size-16">
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
                  Company
                </VegaFont>
                <VegaFont as="span" variant="font-p2-short">
                  {detailsRow.companyName}
                </VegaFont>
              </VegaFlex>
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
                  Region
                </VegaFont>
                <VegaFont as="span" variant="font-p2-short">
                  Horizon {detailsRow.region}
                </VegaFont>
              </VegaFlex>
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label">
                  Application ID
                </VegaFont>
                <VegaFont as="span" variant="font-p2-short">
                  {detailsRow.appUid}
                </VegaFont>
              </VegaFlex>
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label">
                  Email
                </VegaFont>
                <VegaFont as="span" variant="font-p2-short">
                  {maskEmail(detailsRow.email)}
                </VegaFont>
              </VegaFlex>
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label">
                  Record flag
                </VegaFont>
                <VegaFont as="span" data-testid="horizon-details-record-flag" variant="font-p2-short">
                  {getHorizonRecordFlag(detailsRow)}
                </VegaFont>
              </VegaFlex>
            </VegaGrid>
          )}
        </DetailsModal>
      </VegaFlex>
    </VegaCard>
  );
};
